import { Box, LoadingOverlay, MantineSpacing, ScrollArea, Table as MantineTable } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';

import { SortDirection } from '@e-origin/shared';

import { TABLE_COLUMN_WIDTH, TABLE_ITEMS_PER_PAGE, TABLE_SELECTION_COLUMN_WIDTH } from '@constants';
import { mantineTheme } from '@styles';
import classes from './Table.module.css';
import { TableBody } from './TableBody';
import { TableContext } from './TableContext';
import { TableHeader } from './TableHeader';
import { TablePagination } from './TablePagination';

export interface TableColumnProps<T> {
  name: string | React.ReactElement;
  cell: (row: T, rowIdx: number) => React.ReactElement | string | number | Date;
  selector?: string;
  sortable?: boolean;
  width?: number | string;
  style?: any;
  sticky?: 'right' | null;
  hide?: boolean;
}

export interface TableConditionalRowStyles<T> {
  when: boolean | ((row: T) => boolean);
  style: any;
}

interface TableProps<T> {
  dataSource: T[];
  columns: TableColumnProps<T>[];
  total?: number;
  defaultPage?: number;
  defaultPerPage?: number;
  defaultSorting?: string;
  defaultSortingDirection?: SortDirection;
  onPaginationChange?: (page: number, size: number) => void;
  onSortingChange?: (direction: SortDirection, field: string) => void;
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  selectable?: boolean;
  height?: string | number;
  horizontalSpacing?: MantineSpacing;
  verticalSpacing?: MantineSpacing;
  pagination?: boolean;
  conditionalRowStyles?: TableConditionalRowStyles<T>;
  'data-pw'?: string;
}

// eslint-disable-next-line max-statements
export const Table = <T,>({
  dataSource,
  columns,
  total,
  defaultPage = 1,
  defaultPerPage = TABLE_ITEMS_PER_PAGE,
  defaultSorting,
  defaultSortingDirection,
  onPaginationChange,
  onSortingChange,
  onRowClick,
  isLoading,
  selectable,
  height = '100%',
  horizontalSpacing = 'xs',
  verticalSpacing = 'sm',
  pagination = false,
  conditionalRowStyles,
  'data-pw': dataPw,
}: Readonly<TableProps<T>>) => {
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [pageSize, setPageSize] = useState(defaultPerPage);
  const [displayData, setDisplayData] = useState<T[]>([]);
  const computedColumns = useMemo(() => [...columns.filter((col) => !col.sticky), ...columns.filter((col) => col.sticky === 'right')], [columns]);

  useEffect(() => {
    setCurrentPage(defaultPage);
    setPageSize(defaultPerPage);
  }, [defaultPage, defaultPerPage]);

  const calculateMinWidth = () => {
    let minWidth = 0;

    if (selectable) minWidth += TABLE_SELECTION_COLUMN_WIDTH;

    columns.forEach((column) => {
      if (typeof column.width === 'number') {
        minWidth += column.width;
      } else if (typeof column.width === 'string') {
        const widthRegex = /^\d+/;
        const match = widthRegex.exec(column.width);
        if (match) {
          minWidth += parseInt(match[0], 10);
        } else {
          minWidth += TABLE_COLUMN_WIDTH;
        }
      } else {
        minWidth += TABLE_COLUMN_WIDTH;
      }
    });

    return `max(${minWidth}px, 100%)`;
  };

  useEffect(() => {
    if (pagination) {
      setData(dataSource);

      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      setDisplayData(dataSource.slice(startIndex, endIndex));
    } else {
      setData(dataSource);
      setDisplayData(dataSource);
    }
  }, [dataSource, pagination, currentPage, pageSize]);

  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);

    if (pagination) {
      const startIndex = (page - 1) * size;
      const endIndex = startIndex + size;
      setDisplayData(data.slice(startIndex, endIndex));
    }

    if (onPaginationChange) {
      onPaginationChange(page, size);
    }
  };

  const contextValue = useMemo(
    () => ({
      data,
      setData,
      stickyClasses: { right: classes.stickyRight, header: classes.stickyHeader, headerRight: classes.stickyHeaderRight },
    }),
    [data, setData],
  );

  return (
    <TableContext.Provider value={contextValue}>
      <Box className={classes.container} h={height}>
        <ScrollArea className={classes.scrollArea} type="always">
          <LoadingOverlay visible={isLoading} zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} />
          {displayData.length === 0 && !isLoading && (
            <div className={classes.noRecordsMessage} data-pw="table-no-records">
              No records found.
            </div>
          )}
          {displayData.length !== 0 && !isLoading && (
            <MantineTable
              className={classes.table}
              horizontalSpacing={horizontalSpacing}
              verticalSpacing={verticalSpacing}
              highlightOnHover
              highlightOnHoverColor={mantineTheme.colors.blue[0]}
              miw={calculateMinWidth()}
            >
              <TableHeader<T>
                columns={computedColumns}
                onSortingChange={onSortingChange}
                defaultSorting={{ field: defaultSorting, direction: defaultSortingDirection }}
              />
              <TableBody<T> columns={computedColumns} data={displayData} conditionalRowStyles={conditionalRowStyles} onRowClick={onRowClick} />
            </MantineTable>
          )}
        </ScrollArea>
        {(onPaginationChange || pagination) && (
          <TablePagination
            data-pw={dataPw}
            defaultPagination={{ page: currentPage, perPage: pageSize }}
            total={pagination ? data.length : total}
            onPaginationChange={handlePaginationChange}
            disabled={isLoading}
          />
        )}
      </Box>
    </TableContext.Provider>
  );
};
